import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert, AlertDescription } from '@/components/ui/alert';

import { MANUAL_NODES } from '@/shared/config/manual';
import { DataTable } from '@/shared/ui/data-table';

import { getDispatchInProgressColumns, getDispatchInProgressColumnPresets, useDispatchInProgress } from '../model';

export function DispatchInProgressPage() {
  const { t } = useTranslation();
  const {
    tableData,
    totalElements,
    isLoading,
    isFetchingNextPage,
    error,
    isValid,
    hasMore,
    handleLoadMore,
    sortBy,
    sortDirection,
    handleSort
  } = useDispatchInProgress();

  const columns = useMemo(() => getDispatchInProgressColumns(t), [t]);
  const columnPresets = useMemo(() => getDispatchInProgressColumnPresets(t), [t]);

  if (!isValid) {
    return (
      <Alert>
        <AlertDescription>{t('common.errors.invalidParams')}</AlertDescription>
      </Alert>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          {t(
            'dispatch.inprogress.error.loadFailed',
            'Failed to load the in-progress dispatches. Please try again later.'
          )}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold text-foreground">
          {t('dispatch.inprogress.history.title', 'In-progress Dispatches')}
        </h1>
        <p className="text-sm text-muted-foreground">
          {totalElements} {t('gen.numres.label', 'results found')}
        </p>
      </header>

      <DataTable
        data={tableData}
        columns={columns}
        title={t('dispatch.inprogress.history.title', 'In-progress Dispatches')}
        helpTarget={MANUAL_NODES.DISPATCH_IN_PROGRESS_HISTORY}
        tableId="dispatch-in-progress-table"
        isLoading={isLoading}
        isFetchingNextPage={isFetchingNextPage}
        hasMore={hasMore}
        onLoadMore={handleLoadMore}
        sortField={sortBy}
        sortDirection={sortDirection}
        onSort={handleSort}
        enableFullscreen
        enableColumnCustomization
        columnPresets={columnPresets}
      />
    </div>
  );
}
